import {useState} from 'react';
import ProtocolActionsHeader from './ProtocolActionsHeader';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import './ApprovedProtocolActions.scss';
import {ReactComponent as IconLeft} from '../../../../assets/images/icon-arrow-left.svg';
import {useReviewHeaderContext} from '../header/ReviewHeader';
import ProtocolBody from './ProtocolBody';
import ArrModal from '../../../common/arr-modal/ArrModal';
import dayjs from 'dayjs';
import {useDeleteAIProtocolIntelligence} from '../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../shared/constants';
interface ApprovedProtocolActionsProps {
  protocolId: number | null;
}

const isConflictError = (error: unknown): boolean => {
  const apiError = error as {status?: number; response?: {status?: number}};
  return apiError?.status === 409 || apiError?.response?.status === 409;
};

const ApprovedProtocolActions = ({
  protocolId,
}: ApprovedProtocolActionsProps) => {
  const {
    protocolDetails,
    refetchProtocolList,
    resetStates,
    setReviewHeaderToast,
    clearSearchAndFetchProtocolList,
  } = useReviewHeaderContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const {mutateAsync: deleteProtocol, isPending: isDeletePending} =
    useDeleteAIProtocolIntelligence();

  const handleDeleteProtocol = async () => {
    if (!protocolId) return;

    const protocolName = protocolDetails?.protocolName ?? '';

    try {
      await deleteProtocol({protocolId});
      setIsDeleteModalOpen(false);
      // Navigate back to protocol list after delete + list invalidation settle
      resetStates();
      clearSearchAndFetchProtocolList();
      setReviewHeaderToast({
        isOpen: true,
        status: ToastType.Success,
        message: t(
          'ai.review.protocolIntelligence.deleteProtocolModal.deleteProtocolSuccess',
          {protocolName}
        ),
      });
    } catch (error: any) {
      setIsDeleteModalOpen(false);
      if (isConflictError(error)) {
        setReviewHeaderToast({
          isOpen: true,
          status: ToastType.Error,
          message: t(
            'ai.review.protocolIntelligence.deleteProtocolModal.deleteProtocolInUseByJob'
          ),
        });
        return;
      }
      setReviewHeaderToast({
        isOpen: true,
        status: ToastType.Error,
        message: t(
          'ai.review.protocolIntelligence.deleteProtocolModal.deleteProtocolFailed',
          {protocolName}
        ),
      });
    }
  };

  const handleBackButtonClick = () => {
    resetStates();
    refetchProtocolList();
  };

  return (
    <Box className="protocol-actions-container">
      <Box className="protocol-actions-header">
        <ArrButton
          variant="secondary"
          size="medium"
          startIcon={<IconLeft />}
          type="button"
          tabIndex={0}
          dataTestId="protocol-back-button"
          onClick={handleBackButtonClick}
        >
          {t('common.back')}
        </ArrButton>
      </Box>
      <ProtocolActionsHeader setIsDeleteModalOpen={setIsDeleteModalOpen} />
      <ProtocolBody />
      {isDeleteModalOpen && (
        <ArrModal
          title={t('ai.review.protocolIntelligence.deleteProtocolModal.title', {
            protocolName: protocolDetails?.protocolName ?? '',
          })}
          showModal={isDeleteModalOpen}
          onClose={() => {}}
          showClose={false}
          dataTestId="delete-protocol-modal"
          buttons={[
            {
              id: t('common.cancel'),
              label: t('common.cancel'),
              onClick: () => setIsDeleteModalOpen(false),
              variant: 'secondary',
              dataTestId: 'dp-cancel-delete',
            },
            {
              id: t('common.delete'),
              label: t('common.delete'),
              onClick: handleDeleteProtocol,
              variant: 'danger',
              dataTestId: 'dp-confirm-delete',
              loading: isDeletePending,
              disabled: isDeletePending,
            },
          ]}
          className="delete-protocol-modal"
        >
          <Box className="delete-protocol-modal-description">
            <Typography
              className="typo-text body2_Size text-body-strong"
              data-testid="approved-protocol-actions-p1"
            >
              {t('ai.review.protocolIntelligence.deleteProtocolModal.p1')}
            </Typography>
            <Typography
              className="typo-text body2_Size text-body-strong"
              data-testid="approved-protocol-actions-p2"
            >
              {t('ai.review.protocolIntelligence.deleteProtocolModal.p2')}
            </Typography>
          </Box>
        </ArrModal>
      )}
    </Box>
  );
};

export default ApprovedProtocolActions;
